/**
 * Typed API for the world-state surface (Phase WS-1+).
 *
 * Mounts under /api/companies/:cid/series/:sid/{npcs,locations,factions,relationships}.
 * All functions accept the company-scoped Eden Treaty client returned by
 * useCompanyApi() so tenant isolation stays correct.
 *
 * v1 covers NPCs only — locations + factions + relationships will gain typed
 * wrappers when WS-6 and WS-7 land their own UI surfaces.
 */

import type {
  TFactionStanding,
  TFactionType,
  TLocationStatus,
  TLocationType,
  TNpcStatus,
  TWorldRelationshipKind,
} from "@lexicanon/shared"
import type { CompanyApi } from "../../hooks/useCompanyApi.ts"
import { unwrapData } from "./errors.ts"

export type { TFactionStanding, TFactionType, TLocationStatus, TLocationType, TNpcStatus, TWorldRelationshipKind }

export interface INpc {
  id: string
  seriesId: string
  name: string
  aliases: string[]
  role: string | null
  factionId: string | null
  status: TNpcStatus
  lastSeenLocationId: string | null
  description: string | null
  voiceNotes: string | null
  appearance: string | null
  avatarUrl: string | null
  fullBodyUrl: string | null
  pending: boolean
  proposedByMeetingId: string | null
  metadata: string | null
  createdAt: string | null
  updatedAt: string | null
}

export interface INpcCreateBody {
  name: string
  aliases?: string[]
  role?: string
  factionId?: string
  status?: TNpcStatus
  lastSeenLocationId?: string
  description?: string
  voiceNotes?: string
  appearance?: string
  avatarUrl?: string
  fullBodyUrl?: string
}

export interface INpcEditBody {
  name?: string
  aliases?: string[]
  role?: string | null
  factionId?: string | null
  status?: TNpcStatus
  lastSeenLocationId?: string | null
  description?: string | null
  voiceNotes?: string | null
  appearance?: string | null
  avatarUrl?: string | null
  fullBodyUrl?: string | null
}

export async function fetchNpcs(company: CompanyApi, seriesId: string, opts?: { pending?: boolean }): Promise<INpc[]> {
  const query: Record<string, string> = {}
  if (opts?.pending !== undefined) query.pending = String(opts.pending)
  const series = company.series({ id: seriesId })
  const result = await series.npcs.get({ query })
  return unwrapData(result)
}

export async function fetchNpc(company: CompanyApi, seriesId: string, npcId: string): Promise<INpc> {
  const series = company.series({ id: seriesId })
  const result = await series.npcs({ npcId }).get()
  return unwrapData(result)
}

export async function createNpc(company: CompanyApi, seriesId: string, body: INpcCreateBody): Promise<{ id: string }> {
  const series = company.series({ id: seriesId })
  const result = await series.npcs.post(body)
  return unwrapData(result)
}

export async function updateNpc(
  company: CompanyApi,
  seriesId: string,
  npcId: string,
  patch: INpcEditBody,
): Promise<{ success: boolean }> {
  const series = company.series({ id: seriesId })
  const result = await series.npcs({ npcId }).patch(patch)
  return unwrapData(result)
}

export async function acceptNpc(company: CompanyApi, seriesId: string, npcId: string): Promise<{ success: boolean }> {
  const series = company.series({ id: seriesId })
  const result = await series.npcs({ npcId }).accept.patch({})
  return unwrapData(result)
}

export async function deleteNpc(company: CompanyApi, seriesId: string, npcId: string): Promise<{ success: boolean }> {
  const series = company.series({ id: seriesId })
  const result = await series.npcs({ npcId }).delete()
  return unwrapData(result)
}

// ---------------------------------------------------------------------------
// Locations
// ---------------------------------------------------------------------------

export interface ILocation {
  id: string
  seriesId: string
  name: string
  aliases: string[]
  type: TLocationType | null
  parentLocationId: string | null
  description: string | null
  status: TLocationStatus
  imageUrl: string | null
  pending: boolean
  proposedByMeetingId: string | null
  metadata: string | null
  createdAt: string | null
  updatedAt: string | null
}

export interface ILocationCreateBody {
  name: string
  aliases?: string[]
  type?: TLocationType
  parentLocationId?: string
  description?: string
  status?: TLocationStatus
  imageUrl?: string
}

export interface ILocationEditBody {
  name?: string
  aliases?: string[]
  type?: TLocationType | null
  parentLocationId?: string | null
  description?: string | null
  status?: TLocationStatus
  imageUrl?: string | null
}

export async function fetchLocations(
  company: CompanyApi,
  seriesId: string,
  opts?: { pending?: boolean },
): Promise<ILocation[]> {
  const query: Record<string, string> = {}
  if (opts?.pending !== undefined) query.pending = String(opts.pending)
  const series = company.series({ id: seriesId })
  const result = await series.locations.get({ query })
  return unwrapData(result)
}

export async function createLocation(
  company: CompanyApi,
  seriesId: string,
  body: ILocationCreateBody,
): Promise<{ id: string }> {
  const series = company.series({ id: seriesId })
  const result = await series.locations.post(body)
  return unwrapData(result)
}

export async function updateLocation(
  company: CompanyApi,
  seriesId: string,
  locationId: string,
  patch: ILocationEditBody,
): Promise<{ success: boolean }> {
  const series = company.series({ id: seriesId })
  const result = await series.locations({ locationId }).patch(patch)
  return unwrapData(result)
}

export async function acceptLocation(
  company: CompanyApi,
  seriesId: string,
  locationId: string,
): Promise<{ success: boolean }> {
  const series = company.series({ id: seriesId })
  const result = await series.locations({ locationId }).accept.patch({})
  return unwrapData(result)
}

export async function deleteLocation(
  company: CompanyApi,
  seriesId: string,
  locationId: string,
): Promise<{ success: boolean }> {
  const series = company.series({ id: seriesId })
  const result = await series.locations({ locationId }).delete()
  return unwrapData(result)
}

// ---------------------------------------------------------------------------
// Factions
// ---------------------------------------------------------------------------

export interface IFaction {
  id: string
  seriesId: string
  name: string
  aliases: string[]
  type: TFactionType | null
  description: string | null
  leaderNpcId: string | null
  partyStanding: TFactionStanding
  memberNpcIds: string[]
  pending: boolean
  proposedByMeetingId: string | null
  metadata: string | null
  createdAt: string | null
  updatedAt: string | null
}

export interface IFactionCreateBody {
  name: string
  aliases?: string[]
  type?: TFactionType
  description?: string
  leaderNpcId?: string
  partyStanding?: TFactionStanding
  memberNpcIds?: string[]
}

export interface IFactionEditBody {
  name?: string
  aliases?: string[]
  type?: TFactionType | null
  description?: string | null
  leaderNpcId?: string | null
  partyStanding?: TFactionStanding
  memberNpcIds?: string[]
}

export async function fetchFactions(
  company: CompanyApi,
  seriesId: string,
  opts?: { pending?: boolean },
): Promise<IFaction[]> {
  const query: Record<string, string> = {}
  if (opts?.pending !== undefined) query.pending = String(opts.pending)
  const series = company.series({ id: seriesId })
  const result = await series.factions.get({ query })
  return unwrapData(result)
}

export async function createFaction(
  company: CompanyApi,
  seriesId: string,
  body: IFactionCreateBody,
): Promise<{ id: string }> {
  const series = company.series({ id: seriesId })
  const result = await series.factions.post(body)
  return unwrapData(result)
}

export async function updateFaction(
  company: CompanyApi,
  seriesId: string,
  factionId: string,
  patch: IFactionEditBody,
): Promise<{ success: boolean }> {
  const series = company.series({ id: seriesId })
  const result = await series.factions({ factionId }).patch(patch)
  return unwrapData(result)
}

export async function acceptFaction(
  company: CompanyApi,
  seriesId: string,
  factionId: string,
): Promise<{ success: boolean }> {
  const series = company.series({ id: seriesId })
  const result = await series.factions({ factionId }).accept.patch({})
  return unwrapData(result)
}

export async function deleteFaction(
  company: CompanyApi,
  seriesId: string,
  factionId: string,
): Promise<{ success: boolean }> {
  const series = company.series({ id: seriesId })
  const result = await series.factions({ factionId }).delete()
  return unwrapData(result)
}

// ---------------------------------------------------------------------------
// World relationships (NPC ↔ NPC, NPC ↔ faction, faction ↔ faction edges)
// ---------------------------------------------------------------------------

export interface IWorldRelationship {
  id: string
  seriesId: string
  sourceKind: TWorldRelationshipKind
  sourceId: string
  targetKind: TWorldRelationshipKind
  targetId: string
  relationshipType: string
  description: string | null
  pending: boolean
  createdAt: string | null
}

export interface IWorldRelationshipCreateBody {
  sourceKind: TWorldRelationshipKind
  sourceId: string
  targetKind: TWorldRelationshipKind
  targetId: string
  relationshipType: string
  description?: string
}

export interface IWorldRelationshipFilter {
  sourceKind?: TWorldRelationshipKind
  sourceId?: string
  targetKind?: TWorldRelationshipKind
  targetId?: string
}

export async function fetchRelationships(
  company: CompanyApi,
  seriesId: string,
  filter?: IWorldRelationshipFilter,
): Promise<IWorldRelationship[]> {
  const query: Record<string, string> = {}
  if (filter?.sourceKind) query.sourceKind = filter.sourceKind
  if (filter?.sourceId) query.sourceId = filter.sourceId
  if (filter?.targetKind) query.targetKind = filter.targetKind
  if (filter?.targetId) query.targetId = filter.targetId
  const series = company.series({ id: seriesId })
  const result = await series.relationships.get({ query })
  return unwrapData(result)
}

export async function createRelationship(
  company: CompanyApi,
  seriesId: string,
  body: IWorldRelationshipCreateBody,
): Promise<{ id: string }> {
  const series = company.series({ id: seriesId })
  const result = await series.relationships.post(body)
  return unwrapData(result)
}

export async function deleteRelationship(
  company: CompanyApi,
  seriesId: string,
  relationshipId: string,
): Promise<{ success: boolean }> {
  const series = company.series({ id: seriesId })
  const result = await series.relationships({ relationshipId }).delete()
  return unwrapData(result)
}
